import React, { useRef, useState } from 'react';
import LinkButton from './LinkButton';
import { buildZip } from '../utils/zipStore';
import './ImageUpscaler.css';

// Everything happens in the browser: decode, resize on a canvas, pack into a
// ZIP (no compression — the images are compressed already). Nothing is sent
// to or stored on the server.

const MIN_SIDE = 1350;
// Canvas dimension ceiling (Chrome/Safari cap around 32k); guards very
// elongated panoramas from producing an impossible canvas.
const MAX_SIDE = 16384;
// Hard cap on the output file size.
const MAX_BYTES = 20 * 1024 * 1024;
// Quality ladder tried in order until a JPEG fits under MAX_BYTES.
const JPEG_QUALITIES = [0.92, 0.85, 0.78, 0.7, 0.6, 0.5];

let nextId = 1;

// HEIC/HEIF (iPhone photos) only decodes natively in Safari. Everywhere else
// the file is handed to libheif compiled to wasm (the `heic-to` package). The
// decoder is ~3 MB, so it is fetched lazily, once, and only when a HEIC file
// actually shows up. Detection is by the ISO-BMFF `ftyp` brand, not by
// file.type — Chrome on Linux/Windows reports an empty type for .heic.
const HEIC_BRANDS = new Set(['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs', 'mif1', 'msf1']);
async function isHeic(file) {
  if (/\.heic$|\.heif$/i.test(file.name) || /^image\/hei[cf]$/i.test(file.type)) return true;
  if (file.size < 12) return false;
  const head = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const ascii = (from, to) => String.fromCharCode(...head.subarray(from, to));
  return ascii(4, 8) === 'ftyp' && HEIC_BRANDS.has(ascii(8, 12).trim());
}

let heicDecoderPromise = null;
function loadHeicDecoder() {
  if (!heicDecoderPromise) {
    heicDecoderPromise = import('heic-to').catch((e) => {
      heicDecoderPromise = null; // let a later file retry after a network hiccup
      throw new Error(`HEIC decoder failed to load: ${e.message || e}`);
    });
  }
  return heicDecoderPromise;
}

async function decodeHeic(file) {
  const { heicTo } = await loadHeicDecoder();
  try {
    // 'bitmap' hands back the decoded pixels directly — no intermediate JPEG,
    // so the only lossy step stays the final canvas encode.
    return await heicTo({ blob: file, type: 'bitmap' });
  } catch (e) {
    throw new Error(`HEIC decode failed: ${e.message || e}`);
  }
}

async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') {
    return createImageBitmap(file);
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('decode failed'));
      img.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function encodeCanvas(canvas, mime, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('encode failed'))),
      mime,
      quality
    );
  });
}

function replaceExt(name, ext) {
  const dot = name.lastIndexOf('.');
  return (dot > 0 ? name.slice(0, dot) : name) + '.' + ext;
}

function extOf(name) {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : '';
}

function fmtBytes(n) {
  return n >= 1024 * 1024
    ? `${(n / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(n / 1024))} KB`;
}

// Cheap transparency probe: draw the picture onto a tiny canvas and look at
// the alpha channel there (full-size getImageData on a huge photo is slow).
function hasAlpha(bitmap) {
  const size = 32;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(bitmap, 0, 0, size, size);
  const px = ctx.getImageData(0, 0, size, size).data;
  for (let i = 3; i < px.length; i += 4) {
    if (px[i] < 255) return true;
  }
  return false;
}

// Upscale/normalize one file. Returns { outName, data, note, upscaled }.
// Output is always JPEG or PNG, never over MAX_BYTES; originals only pass
// through untouched when they already satisfy all three constraints.
async function processFile(file) {
  const heic = await isHeic(file);
  const bitmap = heic ? await decodeHeic(file) : await decodeImage(file);
  try {
    const w = bitmap.width;
    const h = bitmap.height;
    const isJpeg = !heic && file.type === 'image/jpeg';
    const isPng = !heic && file.type === 'image/png';
    const needResize = Math.min(w, h) < MIN_SIDE;

    if (!needResize && file.size <= MAX_BYTES && (isJpeg || isPng)) {
      // Original bytes go through as-is; only an odd extension gets fixed
      // (.jfif and friends are plain JPEG under a different name).
      const canonical = isJpeg ? ['jpg', 'jpeg'] : ['png'];
      const outName = canonical.includes(extOf(file.name))
        ? file.name
        : replaceExt(file.name, canonical[0]);
      return {
        outName,
        data: new Uint8Array(await file.arrayBuffer()),
        note:
          `${w}×${h} · ${fmtBytes(file.size)} — kept as is` +
          (outName !== file.name ? ', renamed' : ''),
        upscaled: false,
      };
    }

    let scale = needResize ? MIN_SIDE / Math.min(w, h) : 1;
    if (Math.max(w, h) * scale > MAX_SIDE) scale = MAX_SIDE / Math.max(w, h);
    const outW = Math.max(1, Math.round(w * scale));
    const outH = Math.max(1, Math.round(h * scale));

    const draw = (fillWhite) => {
      const canvas = document.createElement('canvas');
      canvas.width = outW;
      canvas.height = outH;
      const ctx = canvas.getContext('2d');
      if (fillWhite) {
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, outW, outH);
      }
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bitmap, 0, 0, outW, outH);
      return canvas;
    };

    // Target format: keep JPEG as JPEG and PNG as PNG; anything exotic
    // (webp, avif, bmp, gif…) becomes PNG when transparency must survive,
    // JPEG otherwise. A PNG that busts the size cap falls back to JPEG too.
    let target = isJpeg ? 'jpeg' : isPng ? 'png' : hasAlpha(bitmap) ? 'png' : 'jpeg';

    let blob = null;
    if (target === 'png') {
      blob = await encodeCanvas(draw(false), 'image/png');
      if (blob.size > MAX_BYTES) target = 'jpeg';
    }
    if (target === 'jpeg') {
      const canvas = draw(true); // JPEG has no alpha — flatten onto white
      for (const quality of JPEG_QUALITIES) {
        blob = await encodeCanvas(canvas, 'image/jpeg', quality);
        if (blob.size <= MAX_BYTES) break;
      }
    }

    const dims = needResize ? `${w}×${h} → ${outW}×${outH}` : `${w}×${h}`;
    return {
      outName: replaceExt(file.name, target === 'jpeg' ? 'jpg' : 'png'),
      data: new Uint8Array(await blob.arrayBuffer()),
      note: `${dims} · ${heic ? 'heic → ' : ''}${target} · ${fmtBytes(blob.size)}`,
      upscaled: needResize,
    };
  } finally {
    if (bitmap.close) bitmap.close();
  }
}

function dedupeName(name, taken) {
  if (!taken.has(name)) return name;
  const dot = name.lastIndexOf('.');
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  let i = 2;
  while (taken.has(`${base} (${i})${ext}`)) i++;
  return `${base} (${i})${ext}`;
}

const ImageUpscaler = ({ onBack, backHref }) => {
  const [items, setItems] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);
  const queueRef = useRef(Promise.resolve());

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const addFiles = (fileList) => {
    const files = Array.from(fileList).filter(
      (f) => f.type.startsWith('image/') || /\.(png|jpe?g|jfif|webp|gif|avif|bmp|tiff?|heic|heif)$/i.test(f.name)
    );
    if (!files.length) return;

    const fresh = files.map((file) => ({
      id: nextId++,
      file,
      name: file.name,
      status: 'pending',
      note: '',
    }));
    setItems((prev) => [...prev, ...fresh]);

    // Process one at a time to keep memory in check with big photos.
    for (const item of fresh) {
      queueRef.current = queueRef.current.then(async () => {
        try {
          const result = await processFile(item.file);
          updateItem(item.id, { status: 'done', ...result });
        } catch (e) {
          updateItem(item.id, { status: 'error', note: e.message || 'failed' });
        }
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer && e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const saveBlob = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const handleDownloadItem = (it) => {
    const mime = it.outName.toLowerCase().endsWith('.png') ? 'image/png' : 'image/jpeg';
    saveBlob(new Blob([it.data], { type: mime }), it.outName);
  };

  const handleDownload = () => {
    const done = items.filter((it) => it.status === 'done');
    if (!done.length) return;
    const taken = new Set();
    const entries = done.map((it) => {
      const name = dedupeName(it.outName, taken);
      taken.add(name);
      return { name, data: it.data };
    });
    saveBlob(buildZip(entries), 'upscaled-images.zip');
  };

  const pending = items.filter((it) => it.status === 'pending').length;
  const done = items.filter((it) => it.status === 'done').length;

  return (
    <div className="upscaler-container">
      <div className="upscaler-header">
        <h1>🖼 Image Upscaler</h1>
        <LinkButton href={backHref} onClick={onBack} className="back-button">Back to Editor</LinkButton>
      </div>

      <p className="upscaler-description">
        Drop images below (several at once is fine). Any image whose shorter side is
        under {MIN_SIDE} px gets enlarged so that side becomes exactly {MIN_SIDE} px.
        Output is always JPEG or PNG (.jfif, webp, HEIC from iPhones and other odd
        formats get converted) and never over 20 MB per file. Images already fine on all three
        counts pass through untouched. Grab everything as one ZIP or download any
        file individually. It all happens in your browser — nothing is uploaded.
      </p>

      <div
        className={`upscaler-dropzone ${dragOver ? 'drag-over' : ''}`}
        onClick={() => inputRef.current && inputRef.current.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <span className="upscaler-dropzone-icon">📁</span>
        <span>Drag &amp; drop images here, or click to choose files</span>
        <input
          ref={inputRef}
          type="file"
          accept="image/*,.heic,.heif"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
        />
      </div>

      {items.length > 0 && (
        <>
          <div className="upscaler-actions">
            <button
              className="upscaler-download-button"
              onClick={handleDownload}
              disabled={done === 0 || pending > 0}
            >
              {pending > 0
                ? `Processing… (${done}/${items.length})`
                : `⬇ Download ZIP (${done} file${done === 1 ? '' : 's'})`}
            </button>
            <button className="upscaler-clear-button" onClick={() => setItems([])}>
              Clear
            </button>
          </div>

          <ul className="upscaler-list">
            {items.map((it) => (
              <li key={it.id} className={`upscaler-item ${it.status}`}>
                <span className="upscaler-item-badge">
                  {it.status === 'pending' && '⏳'}
                  {it.status === 'done' && (it.upscaled ? '↑' : '=')}
                  {it.status === 'error' && '⚠'}
                </span>
                <span className="upscaler-item-name">{it.name}</span>
                <span className="upscaler-item-note">
                  {it.status === 'pending' ? 'processing…' : it.note}
                </span>
                {it.status === 'done' && (
                  <button
                    className="upscaler-item-download"
                    title={`Download ${it.outName}`}
                    onClick={() => handleDownloadItem(it)}
                  >
                    ⬇
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};

export default ImageUpscaler;
